import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertCircle,
  ArrowRight,
  Bell,
  Circle,
  MessageSquare,
  UserCheck,
  UserX,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import DespachoAuxiliarQuickActions from "@/components/despacho/DespachoAuxiliarQuickActions";
import DespachoDirectChatPanel from "@/components/despacho/DespachoDirectChatPanel";
import { openBntAsistente } from "@/modules/asistente/bntAsistenteRoutes";
import {
  DespachoCountBadge,
  DespachoInfoCallout,
  DespachoKpiCard,
} from "@/components/despacho/DespachoUi";
import { buildDirectChatSuggestion } from "@/modules/despacho/despachoChatUi";
import { DESPACHO_AUXILIAR_VISTA_NOTA } from "@/modules/despacho/despachoMockData";
import { useDespachoTeamActivity } from "@/modules/despacho/useDespachoTeamActivity";
import { useDespachoWorkspaceAuxiliaries } from "@/modules/despacho/useDespachoWorkspaceAuxiliaries";
import { useDespachoPanelVisible } from "@/modules/despacho/useDespachoPanelVisible";
import { PANEL_GENERAL_EQUIPO_TAB } from "@/modules/panelGeneralTabs";
import { operationalReportsApi } from "@/modules/despacho/operationalReportsApi";

const PRIORITY_TONE = {
  Crítico: "critical",
  Urgente: "warn",
  Alta: "warn",
  Media: "info",
  Baja: "neutral",
};

const ACTIVITY_ICON = {
  conectado: UserCheck,
  ausente: UserX,
  mensaje: MessageSquare,
  tarea: Bell,
  seguimiento: AlertCircle,
};

function PriorityBadge({ prioridad }) {
  const tone = PRIORITY_TONE[prioridad] || "neutral";
  const classes = {
    critical: "bg-rose-100 text-rose-900 border-rose-200",
    warn: "bg-amber-100 text-amber-900 border-amber-200",
    info: "bg-sky-100 text-sky-900 border-sky-200",
    neutral: "bg-slate-100 text-slate-800 border-slate-200",
  };
  return (
    <Badge variant="outline" className={`font-normal text-[10px] shrink-0 ${classes[tone]}`}>
      {prioridad}
    </Badge>
  );
}

function AccordionCountRow({ children }) {
  return (
    <span className="flex flex-1 items-center justify-between gap-2 pr-2 min-w-0">
      {children}
    </span>
  );
}

/**
 * Resumen compacto bajo las tarjetas del hub: KPIs → Tablero operativo.
 * El detalle de vencidos/tareas vive en /despacho/reportes (no se duplica aquí).
 */
export default function DespachoOperationalSummary() {
  const despachoModuleActive = useDespachoPanelVisible();
  const { auxiliares, loading: auxLoading } = useDespachoWorkspaceAuxiliaries();
  const { actividad, loading: actividadLoading } = useDespachoTeamActivity();
  const [operationalKpis, setOperationalKpis] = useState(null);

  useEffect(() => {
    if (!despachoModuleActive) return;
    (async () => {
      try {
        const data = await operationalReportsApi.getDashboard();
        setOperationalKpis(data?.kpis || null);
      } catch {
        setOperationalKpis(null);
      }
    })();
  }, [despachoModuleActive]);

  const totalPendientesAux = useMemo(
    () => auxiliares.reduce((sum, aux) => sum + (aux.pendientes || 0), 0),
    [auxiliares],
  );

  const [directChat, setDirectChat] = useState(null);
  const [directDraft, setDirectDraft] = useState("");

  const openDirectChat = useCallback((auxiliar, actionId, pendingContext) => {
    const ctx = pendingContext || auxiliar?.items?.[0] || null;
    const suggestion = buildDirectChatSuggestion(actionId, {
      titulo: ctx?.titulo,
      cliente: ctx?.cliente,
      auxiliarNombre: auxiliar?.nombre,
    });
    if (actionId === "urgente" || (ctx?.prioridad === "Crítico" && actionId === "recordar_pendiente")) {
      const urgent = buildDirectChatSuggestion("urgente", {
        titulo: ctx?.titulo,
        cliente: ctx?.cliente,
      });
      setDirectDraft(urgent || suggestion);
    } else {
      setDirectDraft(suggestion);
    }
    setDirectChat({ auxiliar, pendingContext: ctx });
  }, []);

  const handleAuxiliarAction = useCallback(
    (auxiliar, actionId, pendingContext) => {
      openDirectChat(auxiliar, actionId, pendingContext);
    },
    [openDirectChat],
  );

  return (
    <section
      className="space-y-4 pt-2 border-t border-slate-200"
      data-testid="despacho-operational-summary"
      aria-label="Resumen operativo del despacho"
    >
      {despachoModuleActive ? (
        <>
          <div className="flex flex-wrap items-end justify-between gap-2">
            <div>
              <h2 className="text-xs font-bold uppercase tracking-wider text-[#0F2D5C] mb-1">
                Resumen operativo
              </h2>
              <p className="text-xs text-slate-500 m-0">
                4 indicadores. El listado por empresa está en el Tablero operativo.
              </p>
            </div>
            <Button asChild variant="secondary" size="sm" className="rounded-sm h-8 text-xs" data-testid="despacho-op-abrir-tablero">
              <Link to="/despacho/reportes">
                Abrir tablero operativo
                <ArrowRight className="h-3.5 w-3.5 ml-1" aria-hidden />
              </Link>
            </Button>
          </div>

          {operationalKpis ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="despacho-op-kpi-strip">
              <DespachoKpiCard label="Vencen esta semana" value={operationalKpis.due_this_week ?? 0} tone="info" href="/despacho/reportes?report=upcoming_due_dates" />
              <DespachoKpiCard label="Vencidas" value={(operationalKpis.overdue_instances ?? 0) + (operationalKpis.overdue_tasks ?? 0)} tone="warn" href="/despacho/reportes?report=overdue" />
              <DespachoKpiCard label="Tareas pendientes" value={operationalKpis.pending_tasks ?? 0} tone="neutral" href="/despacho/reportes?report=pending_tasks" />
              <DespachoKpiCard label="Sin CFS" value={operationalKpis.clients_no_csf ?? 0} tone="critical" href="/despacho/reportes?report=incomplete_clients" />
            </div>
          ) : null}

          <Accordion
            type="multiple"
            collapsible
            className="rounded-sm border border-slate-200 bg-white divide-y divide-slate-100"
            data-testid="despacho-op-main-accordion"
          >
            <AccordionItem value="pendientes-aux" className="border-0 px-3">
              <AccordionTrigger className="text-xs font-semibold text-[#0F2D5C] hover:no-underline py-3">
                <AccordionCountRow>
                  <span className="text-left">Pendientes por auxiliar</span>
                  <span className="flex items-center gap-1.5 shrink-0">
                    <span className="text-[10px] font-normal text-slate-500">{auxiliares.length} aux.</span>
                    <DespachoCountBadge value={totalPendientesAux} tone={totalPendientesAux >= 10 ? "warn" : "neutral"} />
                  </span>
                </AccordionCountRow>
              </AccordionTrigger>
              <AccordionContent className="pb-3 space-y-3">
                <p className="text-[10px] text-slate-500 m-0">Vista dueño/supervisor — seguimiento individual por auxiliar.</p>

                {auxLoading ? (
                  <p className="text-xs text-slate-500 m-0 py-2">Cargando auxiliares del equipo…</p>
                ) : auxiliares.length === 0 ? (
                  <div className="rounded-sm border border-dashed border-slate-200 bg-slate-50/80 px-3 py-4 text-xs text-slate-600 space-y-2">
                    <p className="m-0">No hay usuarios auxiliares activos en este despacho.</p>
                    <Button asChild variant="outline" size="sm" className="rounded-sm h-8 text-xs">
                      <Link to={PANEL_GENERAL_EQUIPO_TAB}>Administrar usuarios auxiliares</Link>
                    </Button>
                  </div>
                ) : (
                  <Accordion type="single" collapsible className="rounded-sm border border-slate-200 bg-white">
                    {auxiliares.map((aux) => (
                      <AccordionItem key={aux.id} value={aux.id} className="border-slate-200 px-3">
                        <AccordionTrigger className="text-xs font-semibold text-[#0F2D5C] hover:no-underline py-3">
                          <span className="flex flex-1 items-center justify-between gap-2 pr-2 min-w-0">
                            <span className="truncate">{aux.nombre}</span>
                            <DespachoCountBadge value={aux.pendientes} tone={aux.pendientes >= 8 ? "warn" : "neutral"} />
                          </span>
                        </AccordionTrigger>
                        <AccordionContent>
                          {aux.items.length === 0 ? (
                            <p className="text-[11px] text-slate-500 m-0 pb-2">
                              Sin pendientes activos asignados
                              {aux.clients_count > 0 ? ` · ${aux.clients_count} cliente(s) asignado(s)` : ""}.
                            </p>
                          ) : (
                            <ul className="space-y-2 m-0 p-0 list-none pb-2">
                              {aux.items.map((pendiente, idx) => (
                                <li
                                  key={`${aux.id}-${idx}`}
                                  className="rounded-sm border border-slate-100 bg-slate-50/80 px-3 py-2"
                                >
                                  <div className="flex flex-wrap items-start justify-between gap-2">
                                    <div className="min-w-0 text-xs">
                                      <span className="font-medium text-[#0F2D5C]">{pendiente.titulo}</span>
                                      <span className="text-slate-500"> — {pendiente.cliente}</span>
                                    </div>
                                    <div className="flex items-center gap-1 shrink-0">
                                      <PriorityBadge prioridad={pendiente.prioridad} />
                                      <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        className="h-6 text-[9px] px-1.5 rounded-sm text-[#0F2D5C]"
                                        onClick={() => handleAuxiliarAction(aux, "solicitar_avance", pendiente)}
                                        data-testid="despacho-pendiente-seguimiento"
                                      >
                                        Seguimiento
                                      </Button>
                                    </div>
                                  </div>
                                </li>
                              ))}
                            </ul>
                          )}
                          {aux.items.length > 0 ? (
                            <DespachoAuxiliarQuickActions
                              compact
                              pendingContext={aux.items[0]}
                              onAction={(actionId, ctx) => handleAuxiliarAction(aux, actionId, ctx)}
                            />
                          ) : null}
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                )}

                {directChat ? (
                  <DespachoDirectChatPanel
                    auxiliar={directChat.auxiliar}
                    pendingContext={directChat.pendingContext}
                    draftMessage={directDraft}
                    onClose={() => {
                      setDirectChat(null);
                      setDirectDraft("");
                    }}
                  />
                ) : null}

                <DespachoInfoCallout testId="despacho-op-auxiliar-nota">{DESPACHO_AUXILIAR_VISTA_NOTA}</DespachoInfoCallout>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="actividad" className="border-0 px-3" data-testid="despacho-op-actividad">
              <AccordionTrigger className="text-xs font-semibold text-[#0F2D5C] hover:no-underline py-3">
                <AccordionCountRow>
                  <span className="text-left">Actividad del equipo</span>
                  <DespachoCountBadge value={actividad.length} tone="info" />
                </AccordionCountRow>
              </AccordionTrigger>
              <AccordionContent className="pb-3">
                <p className="text-[10px] text-slate-500 m-0 mb-2">Actividad reciente — no sustituye al chat interno del equipo.</p>
                {actividadLoading ? (
                  <p className="text-xs text-slate-500 m-0 py-2">Cargando actividad del equipo…</p>
                ) : actividad.length === 0 ? (
                  <p className="text-xs text-slate-500 m-0 py-2 rounded-sm border border-dashed border-slate-200 bg-slate-50/80 px-3">
                    Sin actividad reciente. Use el chat interno general para comunicarse con el equipo.
                  </p>
                ) : (
                  <ul className="space-y-2 m-0 p-0 list-none">
                    {actividad.map((item) => {
                      const Icon = ACTIVITY_ICON[item.tipo] || Circle;
                      const iconTone =
                        item.tipo === "conectado"
                          ? "text-emerald-600"
                          : item.tipo === "ausente"
                            ? "text-slate-400"
                            : item.tipo === "mensaje"
                              ? "text-sky-600"
                              : item.tipo === "seguimiento"
                                ? "text-amber-600"
                                : "text-[#0F2D5C]";
                      return (
                        <li
                          key={item.id}
                          className="flex items-start gap-2 rounded-sm border border-slate-200 bg-slate-50/80 px-3 py-2 text-xs"
                        >
                          <Icon className={`w-3.5 h-3.5 shrink-0 mt-0.5 ${iconTone}`} aria-hidden />
                          <div className="min-w-0 flex-1">
                            <p className="m-0 text-slate-700 leading-relaxed">{item.texto}</p>
                            <span className="text-[10px] text-slate-400">{item.hora}</span>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
                <div className="pt-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="rounded-sm"
                    onClick={() => openBntAsistente({ tab: "interno" })}
                    data-testid="despacho-op-open-asistente-chat"
                  >
                    Abrir chat en BNT Asistente
                  </Button>
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </>
      ) : null}
    </section>
  );
}
